// The supervised member's side of supervision: what a supervised invite or an
// offer means before accepting, the "Supervised by …" card (controls,
// guardians, requests, "Stop supervision" with its notice period, activity),
// and "Ask" controls that turn a locked action into a request with its state.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useCapabilities } from "../chat/useChatAccount";
import {
  acceptErrorText,
  askErrorText,
  consentLines,
  isPending,
  myControlRows,
  MY_MODE_LABELS,
  myRequestState,
  myRequestText,
  pendingRequests,
  recentRequests,
  releaseText,
  requestFor,
  supervisionPoll,
} from "../supervision/member";
import { modeOf, namesText } from "../supervision/supervision";
import { refreshAfterRequest, useAsk, useMyRequests, useWithdraw } from "../supervision/useMyRequests";
import { useNow } from "../useNow";
import { logKey, supervisionsKey, SupervisionLog } from "./SupervisionPanel";

type Circle = Schemas["Circle"];
type Controls = Schemas["SupervisionControls"];
type Guardian = Schemas["SupervisionGuardian"];
type Request = Schemas["SupervisionRequest"];
type Action = Schemas["SupervisionAction"];
type Params = Schemas["SupervisionRequestParams"];

const NOTE_MAX = 140;
const TICK_MS = 30_000;

const dayText = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/**
 * Controls, guardians and promises, as a supervised invite or an offer shows
 * them before accepting. `current`: the running supervision's controls, so a
 * revision says what changes.
 */
export function SupervisionTerms(props: {
  controls: Controls;
  guardians: readonly Guardian[];
  selfReleaseDays: number;
  circleName: string;
  current?: Controls | null | undefined;
}) {
  const names = namesText(props.guardians);
  return (
    <div className="stack supervision-terms">
      <p>
        <strong>Guardians: {names}.</strong>
      </p>
      <dl className="supervision-control-list">
        {myControlRows(props.controls, true).map((row) => {
          const before = props.current ? modeOf(props.current, row.control) : null;
          return (
            <div key={row.control}>
              <dt>{row.label}</dt>
              <dd>
                {row.text}
                {before && before !== row.mode && <span className="muted"> (now: {MY_MODE_LABELS[before]})</span>}
              </dd>
            </div>
          );
        })}
      </dl>
      <ul className="supervision-promises">
        {consentLines(names, props.circleName, props.selfReleaseDays).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

/** Where one of the caller's requests stands, with Withdraw while it is pending. */
export function RequestState({ request, label }: { request: Request; label?: string }) {
  const nowMs = useNow(TICK_MS);
  const withdraw = useWithdraw();
  const state = myRequestState(request, nowMs);
  return (
    <div className="supervision-request my-request">
      {label && <p>{label}</p>}
      <p className={state.tone === "bad" ? "error" : state.tone === "good" ? "success" : "muted"} role="status">
        {state.text}
      </p>
      {state.pending && (
        <button
          type="button"
          className="link-button"
          disabled={withdraw.isPending}
          onClick={() => withdraw.mutate(request.id)}
        >
          Withdraw request
        </button>
      )}
      {withdraw.error && (
        <p className="error" role="alert">
          {withdraw.error instanceof ApiError && withdraw.error.code === "request_closed"
            ? "This request was already decided or has expired."
            : errorMessage(withdraw.error)}
        </p>
      )}
    </div>
  );
}

/** Sends a request for `action`: an optional note for the guardians, plus any `children` fields. */
export function AskForm(props: {
  action: Action;
  params?: Params | undefined;
  intro?: ReactNode;
  submitLabel?: string;
  children?: ReactNode;
  onSent?: (request: Request) => void;
  onCancel?: () => void;
  /** A mutation owned by a parent that outlives the form (the form unmounts once its request is pending). */
  mutation?: ReturnType<typeof useAsk>;
}) {
  const own = useAsk();
  const ask = props.mutation ?? own;
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");

  function submit(event: FormEvent) {
    event.preventDefault();
    ask.mutate(
      { action: props.action, ...(props.params && { params: props.params }), note: note.trim() || null },
      {
        onSuccess: (request) => props.onSent?.(request),
        // The supervision changed: the buttons should follow it.
        onError: (error) => {
          if (error instanceof ApiError && error.code === "request_not_needed") void refreshAfterRequest(queryClient);
        },
      },
    );
  }

  return (
    <form className="stack ask-form" onSubmit={submit}>
      {props.intro}
      {props.children}
      <label>
        Note for your guardians (optional)
        <input maxLength={NOTE_MAX} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      {ask.error && (
        <p className="error" role="alert">
          {askError(ask.error)}
        </p>
      )}
      <div className="button-row">
        <button type="submit" disabled={ask.isPending}>
          {ask.isPending ? "Sending…" : (props.submitLabel ?? "Send request")}
        </button>
        {props.onCancel && (
          <button type="button" className="secondary" onClick={props.onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export function askError(error: unknown): string {
  if (error instanceof ApiError) {
    const text = askErrorText(error.code, error.status);
    if (text) return text;
  }
  return errorMessage(error);
}

/**
 * A locked action as an "Ask" button: the request's state when there is one
 * (pending, or decided in the last hour), else the button, which opens the
 * request form.
 */
export function AskControl(props: {
  action: Action;
  params?: Params;
  label: string;
  intro?: ReactNode;
  children?: ReactNode;
}) {
  const nowMs = useNow(TICK_MS);
  const requests = useMyRequests();
  const ask = useAsk();
  const [open, setOpen] = useState(false);
  // What the server answered: the new request, or the pending one for the same target, which the
  // client can't always match itself (a join is keyed by the circle, another invite may lead there).
  const sent = ask.data ? (requests.data?.find((r) => r.id === ask.data.id) ?? ask.data) : null;
  const request =
    requestFor(requests.data ?? [], props.action, props.params, nowMs) ??
    (sent ? (recentRequests([sent], [props.action], nowMs)[0] ?? null) : null);
  const pending = request !== null && isPending(request, nowMs);
  // The form unmounts as soon as its request is pending (so its own callbacks may not run): close it then.
  const [wasPending, setWasPending] = useState(pending);
  if (pending !== wasPending) {
    setWasPending(pending);
    if (pending) setOpen(false);
  }
  return (
    <div className="stack ask-control">
      {request && <RequestState request={request} />}
      {!pending &&
        (open ? (
          <AskForm
            action={props.action}
            params={props.params}
            intro={props.intro}
            mutation={ask}
            onCancel={() => setOpen(false)}
          >
            {props.children}
          </AskForm>
        ) : (
          <div className="button-row">
            <button type="button" className="secondary" onClick={() => setOpen(true)}>
              {props.label}
            </button>
          </div>
        ))}
    </div>
  );
}

/** After the member's own supervision write: the circle (and its lists), the panel and the log. */
async function refreshMine(queryClient: ReturnType<typeof useQueryClient>, circleId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["circle", circleId] }),
    queryClient.invalidateQueries({ queryKey: ["circles"] }),
    queryClient.invalidateQueries({ queryKey: supervisionsKey(circleId) }),
    queryClient.invalidateQueries({ queryKey: logKey(circleId) }),
  ]);
}

/** The offer waiting for the caller's answer in `circle`, explained, with Accept and Decline. */
function OfferView({ circle, selfId }: { circle: Circle; selfId: string }) {
  const offer = circle.supervision_offer!;
  const queryClient = useQueryClient();
  const accept = useMutation({
    mutationFn: async (hash: string) =>
      unwrap(
        await api.POST("/circles/{circleId}/supervision/accept", {
          params: { path: { circleId: circle.id } },
          body: { supervision_hash: hash },
        }),
      ),
    onSettled: () => refreshMine(queryClient, circle.id),
  });
  const decline = useMutation({
    mutationFn: async () => {
      const result = await api.DELETE("/circles/{circleId}/members/{userId}/supervision", {
        params: { path: { circleId: circle.id, userId: selfId } },
      });
      // Withdrawn or expired meanwhile is what we wanted.
      if (!result.response.ok && result.response.status !== 404) unwrap(result);
    },
    onSettled: () => refreshMine(queryClient, circle.id),
  });
  const error = accept.error ?? decline.error;
  const revision = circle.supervision !== null;
  return (
    <div className="stack supervision-offer">
      <p>
        {revision
          ? `Your guardians want to change your supervision in ${circle.name}. What would be stricter waits for you to accept, as below; anything that made it less strict already applies.`
          : `An admin of ${circle.name} offered to supervise you here. Nothing changes unless you accept.`}
      </p>
      <SupervisionTerms
        controls={offer.controls}
        guardians={offer.guardians}
        selfReleaseDays={offer.self_release_days}
        circleName={circle.name}
        current={circle.supervision?.controls}
      />
      <p className="muted">The offer is open until {dayText(offer.expires_at)}.</p>
      {error && (
        <p className="error" role="alert">
          {(error instanceof ApiError && acceptErrorText(error.code)) || errorMessage(error)}
        </p>
      )}
      <div className="button-row">
        <button
          type="button"
          disabled={accept.isPending || decline.isPending}
          onClick={() => accept.mutate(offer.supervision_hash)}
        >
          {revision ? "Accept the changes" : "Accept supervision"}
        </button>
        <button
          type="button"
          className="secondary"
          disabled={accept.isPending || decline.isPending}
          onClick={() => decline.mutate()}
        >
          Decline
        </button>
      </div>
    </div>
  );
}

/** The running supervision: guardians, controls, pending requests, self-release and activity. */
/** Requests the circle page shows beside the member's own row instead. */
const ROW_ACTIONS: readonly Action[] = ["pause_circle", "bubble_circle", "leave"];

function SupervisionView(props: { circle: Circle; selfId: string; selfName: string; showRequests: boolean }) {
  const { circle } = props;
  const supervision = circle.supervision!;
  const queryClient = useQueryClient();
  const nowMs = useNow(TICK_MS);
  const capabilities = useCapabilities();
  const requests = useMyRequests();
  const [activity, setActivity] = useState(false);
  const days = capabilities.data?.supervision_self_release_days;
  const release = useMutation({
    mutationFn: async (start: boolean) => {
      const path = { params: { path: { circleId: circle.id } } };
      return unwrap(
        start
          ? await api.POST("/circles/{circleId}/supervision/release", path)
          : await api.DELETE("/circles/{circleId}/supervision/release", path),
      );
    },
    onSettled: () => refreshMine(queryClient, circle.id),
  });

  function stop() {
    const when = days ? `${days === 1 ? "1 day" : `${days} days`} from now` : "after a notice period";
    const question =
      `Stop supervision in ${circle.name}? It ends ${when}, and your guardians are told at once. ` +
      "You can keep it after all until then.";
    if (window.confirm(question)) release.mutate(true);
  }

  const pending = props.showRequests
    ? pendingRequests(requests.data ?? [], nowMs, circle.id).filter((r) => !ROW_ACTIONS.includes(r.action))
    : [];
  const circleName = (id: string) => (id === circle.id ? circle.name : undefined);
  return (
    <div className="stack my-supervision">
      <p>
        {namesText(supervision.guardians)} {supervision.guardians.length === 1 ? "supervises" : "supervise"} you in{" "}
        {circle.name}, since {dayText(supervision.started_at)}. You're told about everything they do.
      </p>
      <dl className="supervision-control-list">
        {myControlRows(supervision.controls).map((row) => (
          <div key={row.control}>
            <dt>{row.label}</dt>
            <dd>{row.text}</dd>
          </div>
        ))}
      </dl>
      <p className="muted">
        Your guardians can pause your sharing with this circle, resume it, or share an approximate area for you here.
        They can't read your chats or see your other circles. SOS works as always.
      </p>
      {pending.length > 0 && (
        <div className="stack supervision-requests">
          <h3>Waiting for a guardian</h3>
          {pending.map((request) => (
            <RequestState key={request.id} request={request} label={myRequestText(request, circleName)} />
          ))}
        </div>
      )}
      {release.error && (
        <p className="error" role="alert">
          {errorMessage(release.error)}
        </p>
      )}
      {supervision.release_at ? (
        <div className="stack">
          <p role="status">{releaseText(supervision.release_at)}</p>
          <div className="button-row">
            <button
              type="button"
              className="secondary"
              disabled={release.isPending}
              onClick={() => release.mutate(false)}
            >
              Keep supervision
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="muted">
            You can stop supervision on your own.{" "}
            {days
              ? `It ends ${days === 1 ? "1 day" : `${days} days`} after you ask, and nobody can change that.`
              : "It ends after a notice period, and nobody can change that."}
          </p>
          <div className="button-row">
            <button type="button" className="secondary danger" disabled={release.isPending} onClick={stop}>
              Stop supervision…
            </button>
          </div>
        </div>
      )}
      <div className="button-row">
        <button type="button" className="link-button" onClick={() => setActivity(!activity)} aria-expanded={activity}>
          {activity ? "Hide activity" : "Activity"}
        </button>
      </div>
      {activity && (
        <SupervisionLog
          circle={circle}
          members={supervision.guardians}
          entry={{ user_id: props.selfId, name: props.selfName }}
          selfId={props.selfId}
        />
      )}
    </div>
  );
}

/**
 * The caller's supervision or offer in `circle` (nothing without either).
 * `showRequests`: list pending account-wide requests this circle decides (the
 * circle page shows the circle's own beside the member's row).
 */
export function MySupervision(props: { circle: Circle; selfId: string; selfName: string; showRequests?: boolean }) {
  const { circle } = props;
  return (
    <>
      {circle.supervision_offer && <OfferView circle={circle} selfId={props.selfId} />}
      {circle.supervision && (
        <SupervisionView
          circle={circle}
          selfId={props.selfId}
          selfName={props.selfName}
          showRequests={props.showRequests ?? true}
        />
      )}
    </>
  );
}

/** The circle page's card: "Supervised by …" or "Supervision offered". */
export function MySupervisionCard(props: { circle: Circle; selfId: string; selfName: string }) {
  const { circle } = props;
  if (!circle.supervision && !circle.supervision_offer) return null;
  return (
    <section className="card stack" id="my-supervision">
      <h2>{circle.supervision ? `Supervised by ${namesText(circle.supervision.guardians)}` : "Supervision offered"}</h2>
      <MySupervision circle={circle} selfId={props.selfId} selfName={props.selfName} />
    </section>
  );
}

/** /account's "Supervision" card: every circle that supervises the caller or offers to, and their pending requests. */
export function AccountSupervisionCard({ selfId, selfName }: { selfId: string; selfName: string }) {
  const nowMs = useNow(TICK_MS);
  const circles = useQuery({
    queryKey: ["circles"],
    queryFn: async () => unwrap(await api.GET("/circles")),
    refetchInterval: (query) => supervisionPoll(query.state.data),
  });
  const list = circles.data ?? [];
  const involved = list.filter((c) => c.supervision || c.supervision_offer);
  const requests = useMyRequests(involved.some((c) => c.supervision));
  if (involved.length === 0) return null;
  const pending = pendingRequests(requests.data ?? [], nowMs);
  const circleName = (id: string) => list.find((c) => c.id === id)?.name;
  return (
    <section className="card stack" id="supervision">
      <h2>Supervision</h2>
      {pending.length > 0 && (
        <div className="stack supervision-requests">
          <h3>Waiting for a guardian</h3>
          {pending.map((request) => (
            <RequestState key={request.id} request={request} label={myRequestText(request, circleName)} />
          ))}
        </div>
      )}
      {involved.map((circle) => (
        <div key={circle.id} className="stack supervision-member">
          <h3>
            <Link to={`/circles/${circle.id}`}>{circle.name}</Link>
            {!circle.supervision && " · supervision offered"}
          </h3>
          <MySupervision circle={circle} selfId={selfId} selfName={selfName} showRequests={false} />
        </div>
      ))}
    </section>
  );
}
